// Create-new-inline (docs/API.md §4.5). A new franchise calling for the first time
// must not force the agent out of the form mid-call (SPEC.md §6), so this posts a
// customer and hands it straight back to the create form — no navigation, no lost
// draft.
//
// WHAT THIS FORM REQUIRES, AND WHY IT CHANGED. Name, phone and position — not
// email. Email was the only required field here until migration 018, and on a
// phone-first desk (SPEC.md §2.1) that was backwards: the people who ring in are
// cashiers, drivers and assistant managers, and plenty of them have no work
// address to give. The old form's answer was an agent inventing one mid-call,
// which puts a fictional address in the natural key of the customer table. What
// the desk actually needs recorded is who the person is, how to ring them back,
// and what they do at the store — which is the new `position` field.
//
// Email stays on the form and stays optional. When it IS given it is still the
// natural key: `customers_email_idx` is unique (filtered, since 018), and the
// duplicate case below is unchanged.
//
// The duplicate case is the interesting one. Agents typing under time pressure
// produce near-duplicates, so the API answers 409 CUSTOMER_EMAIL_EXISTS with the
// *whole* existing customer embedded. That is enough to select it and carry on,
// which is what happens here: the agent gets the person they were looking for plus
// a plain statement that nothing was created, rather than an error to solve while
// someone waits on the line.
//
// Every decision this form makes — which fields are required, the length caps, the
// refused characters, and what actually goes in the body — lives in
// ./newContactForm.js, because there is no DOM test harness in this repository and
// logic inside a component cannot be tested here. This file is state and markup.

import { useEffect, useRef, useState } from 'react';

import { api, ApiError } from '../../api/client.js';
import { buildNewContactBody, initialValues, problemMessage } from './newContactForm.js';

export default function NewCustomerInline({ organization, location, onCreated, onCancel }) {
  const [values, setValues] = useState(() => initialValues(organization));
  const [status, setStatus] = useState('editing');
  const [error, setError] = useState(null);
  // One ref per focusable field so `buildNewContactBody`'s `focus` can put the
  // caret in the box that is actually wrong. An agent with a caller on the line
  // should not have to read an error message to find out which one that is.
  const refs = { name: useRef(null), phone: useRef(null), position: useRef(null), email: useRef(null) };

  useEffect(() => {
    // Name, not email: it is the first field now, and it is the first thing the
    // caller says.
    refs.name.current?.focus();
  }, []);

  function set(key, value) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  async function submit() {
    if (status === 'saving') return;

    const { problems, body, focus } = buildNewContactBody(values, { organization, location });
    if (problems.length) {
      setError({ message: problemMessage(problems) });
      refs[focus]?.current?.focus();
      return;
    }

    setStatus('saving');
    setError(null);
    try {
      const customer = await api.post('/customers', body);
      onCreated(customer, { created: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'CUSTOMER_EMAIL_EXISTS') {
        const existing = caught.details?.existing_customer;
        if (existing) {
          onCreated(existing, { created: false });
          return;
        }
      }
      setStatus('editing');
      setError(caught);
    }
  }

  function handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onCancel();
      return;
    }
    // Enter submits the sub-form rather than the ticket: this block is not a
    // <form>, so nothing else would happen, and re-reaching for the mouse here
    // costs more than anywhere else on the screen.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      submit();
    }
  }

  return (
    <div
      onKeyDown={handleKeyDown}
      // A brand-tinted well rather than bg-sunken, and this is forced rather than
      // decorative: `.field-input` IS bg-sunken in dark, so a sunken container would
      // make the inputs inside it invisible against their own panel. No
      // semantic token means "brand-tinted well", and the brand ramp is
      // theme-independent, so the dark step is written out — brand-900/30 over
      // bg-surface lands on ~#1d2641, distinct from both #161d28 (the card behind)
      // and #222b38 (the inputs in front).
      className="mt-2 rounded-md border p-3 border-brand-200 bg-brand-50/60
                 dark:border-brand-600/60 dark:bg-brand-900/30"
    >
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-ink">New contact</h3>
        {organization ? (
          <span className="text-xs text-ink-secondary">
            {organization.name}
            {location ? ` · ${location.name}` : ''}
          </span>
        ) : (
          <span className="text-xs text-ink-secondary">No organization</span>
        )}
      </div>

      <div className="mt-2 grid gap-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="field-label" htmlFor="new-customer-name">
              Name <span className="text-red-600 dark:text-red-400">*</span>
            </label>
            <input
              id="new-customer-name"
              ref={refs.name}
              className="field-input"
              value={values.name}
              autoComplete="off"
              onChange={(event) => set('name', event.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="new-customer-phone">
              Phone <span className="text-red-600 dark:text-red-400">*</span>
            </label>
            <input
              id="new-customer-phone"
              ref={refs.phone}
              className="field-input"
              value={values.phone}
              autoComplete="off"
              onChange={(event) => set('phone', event.target.value)}
            />
          </div>
        </div>
        {/* Free text on purpose: the same job is "assistant manager", "AM" and
            "2IC" depending on the franchise, so a fixed list would only be wrong
            in a way agents have to work around (migration 018). */}
        <div>
          <label className="field-label" htmlFor="new-customer-position">
            Position <span className="text-red-600 dark:text-red-400">*</span>
          </label>
          <input
            id="new-customer-position"
            ref={refs.position}
            className="field-input"
            value={values.position}
            placeholder="Cashier, driver, assistant manager…"
            autoComplete="off"
            onChange={(event) => set('position', event.target.value)}
          />
        </div>
        {/* Optional since migration 018, and said out loud rather than merely
            implied by the absent asterisk: this is the field agents used to
            invent a value for. */}
        <div>
          <label className="field-label" htmlFor="new-customer-email">
            Email <span className="text-ink-muted font-normal">(optional)</span>
          </label>
          <input
            id="new-customer-email"
            ref={refs.email}
            type="email"
            className="field-input"
            value={values.email}
            autoComplete="off"
            onChange={(event) => set('email', event.target.value)}
          />
          <p className="mt-1 text-xs text-ink-muted">
            Leave blank for a caller with no work address. Without one we cannot reply by email.
          </p>
        </div>
        {/* Free text, and deliberately kept even when an organization is chosen:
            for a caller we have no organization for, this is the only record of
            who they said they were (docs/API.md §4.4). */}
        <div>
          <label className="field-label" htmlFor="new-customer-company">Company</label>
          <input
            id="new-customer-company"
            className="field-input"
            value={values.company}
            autoComplete="off"
            onChange={(event) => set('company', event.target.value)}
          />
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-300">
          {error.message ?? 'The contact could not be created.'}
        </p>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button type="button" className="btn-primary" onClick={submit} disabled={status === 'saving'}>
          {status === 'saving' ? 'Creating…' : 'Create contact'}
        </button>
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <span className="ml-auto text-xs text-ink-muted">Enter to create · Esc to cancel</span>
      </div>
    </div>
  );
}
